import { AssetData } from "../../pages/Assets/CreateAsset/AssetCreationContext";

export type FrameworkID = 'ctb' | 'asvs' | 'top10' | 'wstg' | 'api10';

export interface FrameworkScores {
  ctb: number;
  asvs: number;
  top10: number;
  wstg: number;
  api10: number;
}

export interface GeneratorConfig {
  target: 'web' | 'api' | 'both';
  level: 1 | 2 | 3;
  ctx: {
    auth: boolean;
    roles: boolean;
    multitenant: boolean;
    sensitive_data: boolean;
    file_upload: boolean;
    payment: boolean;
    jwt: boolean;
    mfa: boolean;
    csrf: boolean;
    graphql: boolean;
    webhook: boolean;
  };
  framework?: FrameworkID;
}

/**
 * PATH A: Auto-Detection Score from Asset Data
 */
export function scoreFromAsset(asset: any): FrameworkScores {
  const scores: FrameworkScores = { ctb: 0, asvs: 0, top10: 0, wstg: 0, api10: 0 };
  
  if (!asset) return { ...scores, ctb: 30 }; // Fallback base score for entirely empty assets
  
  // Base bonus for CTB (industry standard benchmark)
  scores.ctb = 30;

  const primaryTypes = asset.primary_type || [];
  const hasApi = primaryTypes.some((t: string) => t?.toLowerCase().includes('api'));
  const hasWeb = primaryTypes.some((t: string) => t?.toLowerCase().includes('web'));

  // 1. Target architecture
  if (hasApi && hasWeb) {
    scores.ctb += 30;
    scores.asvs += 20;
    scores.top10 += 10;
    scores.wstg += 15;
    scores.api10 += 15;
  } else if (hasApi) {
    scores.api10 += 40;
    scores.ctb += 15;
    scores.asvs += 15;
  } else if (hasWeb) {
    scores.top10 += 25;
    scores.ctb += 20;
    scores.asvs += 20;
    scores.wstg += 20;
  }

  // 2. Auth mechanism
  const authRequired = asset.authentication_required || "None";
  const authModel = asset.authorization_model || [];
  if (authRequired !== "None") {
    if (authModel.includes('RBAC') || authModel.includes('ABAC')) {
      scores.ctb += 30;
      scores.asvs += 25;
    } else if (hasApi) {
      scores.api10 += 25;
      scores.asvs += 20;
      scores.ctb += 20;
    } else {
      scores.top10 += 20;
      scores.wstg += 20;
      scores.asvs += 20;
      scores.ctb += 20;
    }
  }

  // 3. Data sensitivity & Compliance
  const classification = (asset.data_classification || "").toLowerCase();
  const complianceFws = asset.compliance_frameworks || [];
  const isRegulated = classification.includes('regulated') || classification.includes('highly') || (complianceFws.length > 0 && !complianceFws.includes('Not Applicable'));

  if (isRegulated) {
    // Aggressive boost for ASVS if regulated, similar to Wizard Q2+Q7
    scores.asvs += 60; 
    scores.top10 += 20;
    scores.ctb += 10;
    
    if (complianceFws.some((f: string) => ['PCI', 'HIPAA', 'GDPR'].some(k => f.includes(k)))) {
       scores.asvs += 15; // Extra boost for high-reg
    }
  }

  // 4. Technology
  const hasGraphql = (asset.includes || []).some((i: string) => i?.toLowerCase().includes('graphql'));
  if (hasGraphql) {
    scores.api10 += 35;
    scores.ctb += 15;
  }

  // 5. Assessment type (based on allowed_testing_types or metadata)
  const testingTypes = asset.allowed_testing_types || [];
  if (isRegulated || testingTypes.some((t: string) => t.toLowerCase().includes('compliance'))) {
    scores.asvs += 15;
  } else {
    scores.ctb += 15;
    scores.wstg += 15;
  }

  return scores;
}

/**
 * PATH B: 7-Question Wizard Scoring
 */
export const WIZARD_QUESTIONS = [
  {
    id: 'target',
    text: "Question 1 — Target Type",
    options: [
      { value: 'web', label: "Web Application", scores: { ctb: 20, asvs: 20, top10: 25, wstg: 20, api10: 0 } },
      { value: 'api', label: "API Only", scores: { ctb: 15, asvs: 15, top10: 5, wstg: 5, api10: 35 } },
      { value: 'both', label: "Web + API (Same Backend)", scores: { ctb: 30, asvs: 20, top10: 10, wstg: 15, api10: 15 } },
      { value: 'mobile', label: "Mobile Backend / Microservices", scores: { ctb: 10, asvs: 10, top10: 5, wstg: 5, api10: 35 } },
      { value: 'unknown', label: "Not Sure", scores: { ctb: 20, asvs: 15, top10: 15, wstg: 15, api10: 10 } }
    ]
  },
  {
    id: 'goal',
    text: "Question 2 — Assessment Goal",
    options: [
      { value: 'compliance', label: "Compliance Audit (PCI/HIPAA/SOC2)", scores: { ctb: 5, asvs: 35, top10: 15, wstg: 5, api10: 5 } },
      { value: 'pentest', label: "Penetration Test", scores: { ctb: 25, asvs: 15, top10: 15, wstg: 25, api10: 10 } },
      { value: 'bug_bounty', label: "Bug Bounty Preparation", scores: { ctb: 20, asvs: 10, top10: 20, wstg: 20, api10: 15 } },
      { value: 'risk', label: "Quick Risk Review", scores: { ctb: 15, asvs: 5, top10: 35, wstg: 5, api10: 10 } },
      { value: 'full', label: "Full Security Verification", scores: { ctb: 20, asvs: 35, top10: 10, wstg: 20, api10: 10 } }
    ]
  },
  {
    id: 'api_tech',
    text: "Question 3 — API Technology",
    options: [
      { value: 'rest_ui', label: "REST API with UI", scores: { ctb: 25, asvs: 15, top10: 15, wstg: 15, api10: 20 } },
      { value: 'rest_only', label: "REST API Only", scores: { ctb: 10, asvs: 10, top10: 5, wstg: 5, api10: 40 } },
      { value: 'graphql', label: "GraphQL", scores: { ctb: 15, asvs: 10, top10: 5, wstg: 10, api10: 35 } },
      { value: 'none', label: "No API / Web only", scores: { ctb: 20, asvs: 20, top10: 25, wstg: 25, api10: 0 } },
      { value: 'unknown', label: "Unknown", scores: { ctb: 25, asvs: 20, top10: 20, wstg: 15, api10: 10 } }
    ]
  },
  {
    id: 'auth',
    text: "Question 4 — Authentication Mechanism",
    options: [
      { value: 'session', label: "Cookie / Session-based", scores: { ctb: 20, asvs: 20, top10: 25, wstg: 25, api10: 5 } },
      { value: 'jwt', label: "JWT / OAuth2 Tokens", scores: { ctb: 20, asvs: 20, top10: 10, wstg: 10, api10: 30 } },
      { value: 'apikey', label: "API Key", scores: { ctb: 15, asvs: 15, top10: 10, wstg: 10, api10: 35 } },
      { value: 'none', label: "None / No auth", scores: { ctb: 20, asvs: 15, top10: 30, wstg: 20, api10: 10 } },
      { value: 'mixed', label: "Mixed / Multiple", scores: { ctb: 30, asvs: 25, top10: 15, wstg: 15, api10: 15 } }
    ]
  },
  {
    id: 'sensitivity',
    text: "Question 5 — Data Sensitivity",
    options: [
      { value: 'low', label: "Public / Low sensitivity", scores: { ctb: 15, asvs: 10, top10: 30, wstg: 20, api10: 15 } },
      { value: 'internal', label: "Internal business data", scores: { ctb: 20, asvs: 20, top10: 20, wstg: 20, api10: 10 } },
      { value: 'pii', label: "Customer PII", scores: { ctb: 20, asvs: 30, top10: 15, wstg: 15, api10: 10 } },
      { value: 'financial', label: "Financial / Payment data", scores: { ctb: 20, asvs: 35, top10: 20, wstg: 10, api10: 10 } },
      { value: 'healthcare', label: "Healthcare / Regulated", scores: { ctb: 15, asvs: 40, top10: 15, wstg: 10, api10: 10 } }
    ]
  },
  {
    id: 'timeframe',
    text: "Question 6 — Available Timeframe",
    options: [
      { value: 'quick', label: "Quick (< 1 day)", scores: { ctb: 15, asvs: 5, top10: 35, wstg: 5, api10: 20 } },
      { value: 'standard', label: "Standard (1–3 days)", scores: { ctb: 30, asvs: 15, top10: 15, wstg: 20, api10: 15 } },
      { value: 'comprehensive', label: "Comprehensive (3–5 days)", scores: { ctb: 20, asvs: 25, top10: 10, wstg: 30, api10: 10 } },
      { value: 'extended', label: "Extended / Red team (5+ days)", scores: { ctb: 15, asvs: 35, top10: 5, wstg: 35, api10: 10 } }
    ]
  },
  {
    id: 'compliance',
    text: "Question 7 — Compliance Requirement",
    options: [
      { value: 'high_reg', label: "Yes – PCI/GDPR/HIPAA", scores: { ctb: 5, asvs: 40, top10: 15, wstg: 5, api10: 5 } },
      { value: 'standard_reg', label: "Yes – SOC2 / ISO 27001", scores: { ctb: 10, asvs: 35, top10: 20, wstg: 10, api10: 5 } },
      { value: 'internal', label: "Internal policy only", scores: { ctb: 30, asvs: 15, top10: 20, wstg: 20, api10: 10 } },
      { value: 'none', label: "No compliance requirement", scores: { ctb: 25, asvs: 10, top10: 20, wstg: 25, api10: 20 } }
    ]
  }
];

/**
 * Framework Suggestion Logic
 */
export function getFrameworkSuggestion(scores: FrameworkScores) {
  // Sort by score DESC, then by framework ID ASC for ties
  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const maxScore = sorted[0][1];
  const winner = sorted[0][0] as FrameworkID;

  const ranked = sorted.map(([id, score]) => {
    const confidence = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
    let level: 'High' | 'Medium' | 'Base' = 'Base';
    if (confidence >= 80) level = 'High';
    else if (confidence >= 50) level = 'Medium';

    return { id: id as FrameworkID, score, confidence, level };
  });

  return { winner, ranked };
}

/**
 * Filter Pipeline Gates
 */
export function filterTestCases(all: any[], config: GeneratorConfig) {
  return all.filter(tc => {
    // GATE 1: Target Type
    if (config.target === 'web' && !tc.is_web) return false;
    if (config.target === 'api' && !tc.is_api) return false;
    
    // GATE 2: Level
    const levels = [];
    if (tc.l1) levels.push(1);
    if (tc.l2) levels.push(2);
    if (tc.l3) levels.push(3);
    if (!levels.some(l => l <= config.level)) return false;

    // GATE 3: Auth
    if (tc.requires_auth && !config.ctx.auth) return false;
    
    // GATE 4: Roles
    if (tc.requires_role && !config.ctx.roles) return false;

    // GATE 5: Conditionals
    if (tc.conditional_flag && !config.ctx[tc.conditional_flag as keyof typeof config.ctx]) return false;

    return true;
  });
}
